import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShoppingBag, Heart, Users, Sparkles, Briefcase
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import type { HomePage } from '../lib/homeDefaults';
import { cms } from '../i18n/localize';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import LeverageSection from '../components/LeverageSection';
import TestimonialsSection from '../components/TestimonialsSection';
import StudentStoriesSection from '../components/StudentStoriesSection';
import MediaCoverageSection from '../components/MediaCoverageSection';

export interface HeroSection {
  type: 'hero';
  title: string;
  subtitle: string;
  description: string;
  backgroundImage: string;
  ctaPrimary: { label: string; url: string };
  ctaSecondary: { label: string; url: string };
}

export interface MissionSection {
  type: 'mission';
  title: string;
  description: string;
  values: { title: string; description: string }[];
}

export interface AboutSection {
  type: 'about';
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

export interface VocationalTrainingSection {
  type: 'vocationalTraining';
  title: string;
  description: string;
  logoUrl: string;
  features: string[];
  imageUrl: string;
}

export interface CorporateSupportSection {
  type: 'corporateSupport';
  title: string;
  description: string;
  imageUrl: string;
}

export interface MediaCoverageSection {
  type: 'mediaCoverage';
  title: string;
  description: string;
  items: { imageUrl: string; caption: string }[];
}

export interface SuccessStoriesSection {
  type: 'successStories';
  title: string;
  quotes: { quote: string; author: string }[];
}

export interface FooterSection {
  type: 'footer';
  logoUrl: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  facebookUrl: string;
  copyright: string;
  facebookIcon: string;
}

export interface LeverageSectionData {
  type: 'leverage';
  badge: string;
  title: string;
  description: string;
  leftImageUrl: string;
  rightImageUrl: string;
  leftTitle: string;
  leftDescription: string;
  rightTitle: string;
  rightDescription: string;
  beforeLabel: string;
  afterLabel: string;
}

export type BrandStorySection =
  | HeroSection
  | MissionSection
  | AboutSection
  | VocationalTrainingSection
  | CorporateSupportSection
  | MediaCoverageSection
  | SuccessStoriesSection
  | FooterSection
  | LeverageSectionData;

export interface BrandStoryPage {
  title: string;
  description: string;
  sections: BrandStorySection[];
  meta?: Record<string, any>;
  isActive?: boolean;
}

export const DEFAULT_BRAND_STORY_SECTIONS: BrandStorySection[] = [
  {
    type: 'hero',
    title: '<b style="display:block; font-size:0.4em; font-weight:500; letter-spacing:0.05em; margin-bottom:0.4rem;">WELCOME TO GOOD STORE</b> <b style="display:block;">好餸 - 鄰里店</b>',
    subtitle: '扶助 SEN 青年成就職夢',
    description: '每一個人都有不完善的地方，天生我才，往往只欠發展機會及空間，期待大家更多關注及支持本會的共融職訓發展工作。',
    backgroundImage: 'https://static.wixstatic.com/media/a936fed67f61499e9facde45392e47c8.jpg/v1/fill/w_1920,h_835,al_c,q_85,enc_avif,quality_auto/a936fed67f61499e9facde45392e47c8.jpg',
    ctaPrimary: { label: '立即選購', url: '/' },
    ctaSecondary: { label: '聯絡我們', url: '#contact' },
  },
  {
    type: 'mission',
    title: '以學為本．連結社區',
    description: '不是銷售「商業培訓服務」，而是為 SEN 青年創造被看見、被接納、被支持的學習與成長空間。在此過程中，我們一方面為基層及有需要人士提供餐膳關懷與社區支援；另一方面，讓公眾和企業親身接觸 SEN 青年的能力與潛能，減少標籤，推動社會共融。',
    values: [
      { title: '職業訓練', description: '為 SEN 青年創造實習及工作機會，發展職場技能與自信。' },
      { title: '社區飯堂', description: '以「好餸社企．社區飯堂」連結社區，提供更有尊嚴的食物支援。' },
      { title: '企業共融', description: '讓公眾和企業親身接觸 SEN 青年的能力與潛能，推動社會共融。' },
    ],
  },
  {
    type: 'about',
    title: '我們相信「一頓飯的力量」',
    description: '我們正努力於全港社區，建立屬於SEN青年的實戰訓練場，可以包容不同能力學生重覆練習及累積經驗，裝備 SEN 青年去建立工作技能與自信。「好餸社企．社區飯堂」是社區中互相扶持的連結點，發揮共融互助精神，扶助基層及有需要人士，提供更有尊嚴的食物支援。\n\n一個可持續的職訓及共融空間，讓被忽略的能力得以發光的平台，讓努力和能力被看見及肯定。',
    imageUrl: 'https://static.wixstatic.com/media/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png/v1/fill/w_716,h_402,al_c,lg_1,q_85,enc_avif,quality_auto/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png',
    imageAlt: '好餸社企活動',
  },
  {
    type: 'leverage',
    badge: '善的槓桿',
    title: '一份捐款，兩份改變',
    description: '傳統食物援助只解決「今天吃什麼」；好餸社企讓每份餐膳同時成為 SEN 青年的職場練習場，創造溫飽與自立雙重回饋。',
    leftImageUrl: 'https://static.wixstatic.com/media/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png/v1/fill/w_716,h_402,al_c,lg_1,q_85,enc_avif,quality_auto/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png',
    rightImageUrl: 'https://static.wixstatic.com/media/6ff1e1_90f6726bec9b4cd3a3ad63791dafd4f2~mv2.jpg/v1/fill/w_388,h_388,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/6ff1e1_90f6726bec9b4cd3a3ad63791dafd4f2~mv2.jpg',
    leftTitle: '溫飽基層家庭',
    leftDescription: '為有需要家庭提供新鮮熱騰的餐膳，讓每個人都能有尊嚴地吃飽，減輕生活壓力。',
    rightTitle: '培育 SEN 青年自立',
    rightDescription: '在中央廚房與社區飯堂的實戰崗位中，學習餐飲技能、建立自信，走向自立就業。',
    beforeLabel: '傳統慈善：送飯 → 吃飽',
    afterLabel: '好餸社企：送飯 → 吃飽 + 職訓 + 就業',
  },
  {
    type: 'footer',
    logoUrl: 'https://shop.deliciousexpress.com.hk/media/story/1784358862089_logo-delicious-express.jpg',
    address: '新界葵涌永健路16至20號高威工業中心A座501室',
    phone: '35946130',
    whatsapp: '57961378',
    email: 'support@deliciousexpress.com.hk',
    facebookUrl: 'https://www.facebook.com/deliciousexpress.hk',
    copyright: '© 2026 by 好餸 Delicious Express',
    facebookIcon: '',
  },
];

function classNames(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function SafeImage({
  src,
  alt,
  className,
  fallbackClassName,
}: {
  src?: string;
  alt: string;
  className?: string;
  fallbackClassName?: string;
}) {
  const [error, setError] = React.useState(false);
  if (!src || error) {
    return (
      <div
        className={classNames(
          'flex items-center justify-center bg-gray-100 text-gray-400',
          fallbackClassName || className
        )}
      >
        <span className="text-xs">{alt}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
}

const BrandStory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState<BrandStoryPage | null>(null);
  const [homePage, setHomePage] = useState<HomePage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const [brandRes, homeRes] = await Promise.all([
          apiFetch('/api/public/cms/pages/brand-story'),
          apiFetch('/api/public/cms/pages/home'),
        ]);

        if (homeRes.ok) {
          const homeData = await homeRes.json();
          setHomePage(homeData);
        }

        if (brandRes.ok) {
          const data = await brandRes.json();
          if (data && Array.isArray(data.sections) && data.sections.length > 0) {
            setPage({
              title: data.title || '品牌故事',
              description: data.description || '',
              sections: data.sections,
              meta: data.meta || {},
              isActive: data.isActive ?? true,
            });
          } else {
            setPage({
              title: data.title || '品牌故事',
              description: data.description || '',
              sections: DEFAULT_BRAND_STORY_SECTIONS,
              meta: data.meta || {},
              isActive: data.isActive ?? true,
            });
          }
        } else {
          setPage({
            title: '品牌故事',
            description: '',
            sections: DEFAULT_BRAND_STORY_SECTIONS,
            meta: {},
            isActive: true,
          });
        }
      } catch {
        setPage({
          title: '品牌故事',
          description: '',
          sections: DEFAULT_BRAND_STORY_SECTIONS,
          meta: {},
          isActive: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  useEffect(() => {
    if (page?.title) {
      document.title = `${cms('brandStory.pageTitle', page.title)} | ${t('common:brand.suffix')}`;
    }
  }, [page?.title, i18n.language, t]);

  const sections = page?.sections || DEFAULT_BRAND_STORY_SECTIONS;
  const leverageSection =
    sections.find((s): s is LeverageSectionData => s.type === 'leverage') ||
    homePage?.sections?.find((s): s is LeverageSectionData => s.type === 'leverage') ||
    DEFAULT_BRAND_STORY_SECTIONS.find((s): s is LeverageSectionData => s.type === 'leverage');
  const footerSection = sections.find((s): s is FooterSection => s.type === 'footer');

  const renderDescription = (text?: string) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => (
      <p key={i} className={classNames(i > 0 && 'mt-3')}>
        {line}
      </p>
    ));
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-stone-800">
      <SiteHeader />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-stone-500">
          <div className="w-10 h-10 border-4 border-orange-200 border-t-[#C2410C] rounded-full animate-spin" />
          <p className="mt-4">{t('content:brandStory.loading')}</p>
        </div>
      ) : (
        <main>
          {sections.map((section, idx) => {
            // Skip sections that are rendered explicitly outside the map.
            if (section.type === 'mediaCoverage') return null;
            if (section.type === 'successStories') return null;
            if (section.type === 'leverage') return null;
            if (section.type === 'footer') return null;

            switch (section.type) {
              case 'hero': {
                const s = section as HeroSection;
                return (
                  <section
                    key={idx}
                    className="relative bg-brand-700 text-white overflow-hidden"
                  >
                    <div className="absolute inset-0">
                      <SafeImage
                        src={s.backgroundImage}
                        alt={t('content:brandStory.heroBgAlt')}
                        className="w-full h-full object-cover opacity-40"
                        fallbackClassName="w-full h-full"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-900/80 to-brand-700/40" />
                    </div>
                    <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
                      <div className="max-w-2xl">
                        <p className="text-brand-100 text-sm md:text-base font-medium mb-3 tracking-wider">
                          {cms('brandStoryCms.hero.subtitle', s.subtitle)}
                        </p>
                        <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                          {cms('brandStoryCms.hero.title', s.title)}
                        </h1>
                        <div className="text-base md:text-lg text-white/90 leading-relaxed mb-8">
                          {renderDescription(cms('brandStoryCms.hero.description', s.description))}
                        </div>
                        <div className="flex flex-wrap gap-4">
                          <a
                            href={s.ctaPrimary.url}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand-700 font-bold rounded-full hover:bg-brand-50 transition-colors shadow-lg"
                          >
                            <ShoppingBag size={18} />
                            {cms('brandStoryCms.hero.ctaPrimary', s.ctaPrimary.label)}
                          </a>
                          <a
                            href={s.ctaSecondary.url}
                            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white/80 text-white font-bold rounded-full hover:bg-white/10 transition-colors"
                          >
                            {cms('brandStoryCms.hero.ctaSecondary', s.ctaSecondary.label)}
                          </a>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'mission': {
                const s = section as MissionSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
                    <div className="max-w-6xl mx-auto">
                      <div className="text-center max-w-3xl mx-auto mb-12">
                        <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-4">
                          <Heart size={20} />
                          <span>{t('content:brandStory.believe')}</span>
                        </div>
                        <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                          {cms('brandStoryCms.mission.title', s.title)}
                        </h2>
                        <div className="text-stone-600 leading-relaxed">
                          {renderDescription(cms('brandStoryCms.mission.description', s.description))}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {s.values.map((v, i) => (
                          <div
                            key={i}
                            className="bg-[#FFFBF5] rounded-2xl p-6 md:p-8 border border-stone-100 hover:shadow-lg transition-shadow"
                          >
                            <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center mb-4">
                              <Sparkles className="text-brand-600" size={24} />
                            </div>
                            <h3 className="text-lg font-bold text-stone-800 mb-3">
                              {cms(`brandStoryCms.mission.valueTitles.${i}`, v.title)}
                            </h3>
                            <p className="text-stone-600 text-sm leading-relaxed">
                              {cms(`brandStoryCms.mission.valueDescriptions.${i}`, v.description)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                );
              }

              case 'about': {
                const s = section as AboutSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
                    <div className="max-w-6xl mx-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div className="order-2 lg:order-1">
                          <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-4">
                            <Users size={20} />
                            <span>{t('content:brandStory.aboutKicker')}</span>
                          </div>
                          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                            {cms('brandStoryCms.about.title', s.title)}
                          </h2>
                          <div className="text-stone-600 leading-relaxed">
                            {renderDescription(cms('brandStoryCms.about.description', s.description))}
                          </div>
                        </div>
                        <div className="order-1 lg:order-2">
                          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-white">
                            <SafeImage
                              src={s.imageUrl}
                              alt={s.imageAlt}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'vocationalTraining': {
                const s = section as VocationalTrainingSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
                    <div className="max-w-6xl mx-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-white mb-6 lg:mb-0">
                            <SafeImage
                              src={s.imageUrl}
                              alt={s.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-14 h-14 bg-white rounded-xl shadow-sm p-1.5 overflow-hidden flex-shrink-0">
                              <SafeImage
                                src={s.logoUrl}
                                alt={t('content:brandStory.logoAlt')}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <h2 className="text-2xl md:text-4xl font-bold text-stone-800">
                              {s.title}
                            </h2>
                          </div>
                          <div className="text-stone-600 leading-relaxed mb-8">
                            {renderDescription(s.description)}
                          </div>
                          <ul className="space-y-4">
                            {s.features.map((feature, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="mt-1 w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                                  <Sparkles size={14} className="text-brand-600" />
                                </span>
                                <span className="text-stone-700 text-sm md:text-base leading-relaxed">
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'corporateSupport': {
                const s = section as CorporateSupportSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
                    <div className="max-w-6xl mx-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                          <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-4">
                            <Briefcase size={20} />
                            <span>{t('content:brandStory.corporateKicker')}</span>
                          </div>
                          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                            {s.title}
                          </h2>
                          <div className="text-stone-600 leading-relaxed">
                            {renderDescription(s.description)}
                          </div>
                        </div>
                        <div>
                          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-white">
                            <SafeImage
                              src={s.imageUrl}
                              alt={s.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              default:
                return null;
            }
          })}

          {/* 善的槓桿：固定在權威見證之上 */}
          {leverageSection && (
            <LeverageSection
              badge={leverageSection.badge}
              title={leverageSection.title}
              description={leverageSection.description}
              leftImageUrl={leverageSection.leftImageUrl}
              rightImageUrl={leverageSection.rightImageUrl}
              leftTitle={leverageSection.leftTitle}
              leftDescription={leverageSection.leftDescription}
              rightTitle={leverageSection.rightTitle}
              rightDescription={leverageSection.rightDescription}
              beforeLabel={leverageSection.beforeLabel}
              afterLabel={leverageSection.afterLabel}
            />
          )}
          <TestimonialsSection />
          <StudentStoriesSection />
          <MediaCoverageSection />
          <SiteFooter section={footerSection} />
        </main>
      )}
    </div>
  );
};

export default BrandStory;
